import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom'
import { Save, ArrowLeft, Eye, Calendar, Clock, User, FileText } from 'lucide-react'
import { NEWS_CATEGORIES } from '@/modules/news/config/news.config'
import { useAdminArticlesStore, type AdminArticle } from '../../../state/articlesStore'
import { useToast } from '../../components/Toast'

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 100)
}

function todayVN(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}

type FormState = Omit<AdminArticle, 'id'>

const EMPTY: FormState = {
  slug: '', title: '', excerpt: '', image: '',
  date: todayVN(), author: 'Xe Điện Việt', category: 'Tin sản phẩm',
  readTime: 5, content: '', status: 'draft',
}

export function ArticleFormPage() {
  const { id } = useParams<{ id?: string }>()
  const navigate = useNavigate()
  const push = useToast((s) => s.push)
  const articles = useAdminArticlesStore((s) => s.articles)
  const add = useAdminArticlesStore((s) => s.add)
  const update = useAdminArticlesStore((s) => s.update)
  const getById = useAdminArticlesStore((s) => s.getById)

  const isEdit = Boolean(id && id !== 'moi')
  const existing = isEdit && id ? getById(id) : undefined

  const [v, setV] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [slugTouched, setSlugTouched] = useState(false)
  const [tab, setTab] = useState<'write' | 'preview'>('write')

  useEffect(() => {
    if (isEdit && existing) {
      const { id: _, ...rest } = existing
      setV(rest)
      setSlugTouched(true)
    }
  }, [isEdit, existing])

  const upd = <K extends keyof FormState>(k: K, val: FormState[K]) => {
    setV((s) => ({ ...s, [k]: val }))
    setErrors((e) => ({ ...e, [k as string]: '' }))
  }

  // Auto slug khi gõ title (chỉ khi chưa touch)
  useEffect(() => {
    if (!isEdit && !slugTouched && v.title) {
      setV((s) => ({ ...s, slug: slugify(v.title) }))
    }
  }, [v.title, isEdit, slugTouched])

  // Auto readTime từ content (200 từ/phút)
  useEffect(() => {
    const words = (v.content ?? '').trim().split(/\s+/).filter(Boolean).length
    if (words > 0) {
      const minutes = Math.max(1, Math.round(words / 200))
      if (minutes !== v.readTime) setV((s) => ({ ...s, readTime: minutes }))
    }
  }, [v.content]) // eslint-disable-line

  const slugDupe = useMemo(() => {
    if (!v.slug) return false
    return articles.some((a) => a.slug === v.slug && a.id !== (existing?.id ?? ''))
  }, [v.slug, articles, existing])

  const inputCls = (err?: string) => [
    'w-full h-11 rounded-md border bg-surface px-3.5 text-sm outline-none transition',
    err ? 'border-danger focus:border-danger' : 'border-border focus:border-brand-500',
  ].join(' ')

  const handleSubmit = async (e: React.FormEvent, publish = false) => {
    e.preventDefault()
    const errs: Record<string, string> = {}
    if (!v.title.trim())   errs.title = 'Vui lòng nhập tiêu đề'
    if (!v.slug.trim())    errs.slug  = 'Slug không được rỗng'
    if (slugDupe)          errs.slug  = 'Slug đã tồn tại'
    if (!v.excerpt.trim()) errs.excerpt = 'Vui lòng nhập mô tả ngắn'
    if (!v.image.trim())   errs.image = 'Cần URL ảnh đại diện'
    if (Object.keys(errs).length) { setErrors(errs); return }

    const payload: FormState = { ...v, status: publish ? 'published' : v.status }
    if (isEdit && existing) {
      await update(existing.id, payload)
      push('success', publish ? `Đã xuất bản "${payload.title}"` : `Đã cập nhật "${payload.title}"`)
    } else {
      await add(payload)
      push('success', publish ? `Đã xuất bản "${payload.title}"` : `Đã lưu nháp "${payload.title}"`)
    }
    navigate('/admin/tin-tuc')
  }

  return (
    <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <Link to="/admin/tin-tuc" className="text-sm text-muted hover:text-brand-600 inline-flex items-center gap-1 mb-1">
            <ArrowLeft className="w-4 h-4" /> Danh sách bài viết
          </Link>
          <h1 className="text-2xl font-bold text-text">
            {isEdit ? 'Sửa bài viết' : 'Viết bài mới'}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/tin-tuc" className="h-11 px-5 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm">
            Hủy
          </Link>
          <button type="submit" className="h-11 px-5 rounded-md border border-border text-text hover:bg-slate-50 font-medium text-sm inline-flex items-center gap-2">
            <FileText className="w-4 h-4" /> Lưu nháp
          </button>
          <button
            type="button"
            onClick={(e) => handleSubmit(e as unknown as React.FormEvent, true)}
            className="h-11 px-5 rounded-md bg-brand-600 hover:bg-brand-700 text-white font-semibold text-sm inline-flex items-center gap-2"
          >
            <Save className="w-4 h-4" /> Xuất bản
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        <div className="space-y-5">
          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tiêu đề <span className="text-danger">*</span></div>
              <input value={v.title} onChange={(e) => upd('title', e.target.value)} className={inputCls(errors.title) + ' !h-12 !text-base font-semibold'} placeholder="Nhập tiêu đề bài viết..." />
              {errors.title && <div className="text-xs text-danger mt-1">{errors.title}</div>}
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Slug URL <span className="text-danger">*</span></div>
              <input
                value={v.slug}
                onChange={(e) => { setSlugTouched(true); upd('slug', slugify(e.target.value)) }}
                className={inputCls(errors.slug)}
                placeholder="top-5-xe-may-dien-2026"
              />
              <div className="text-xs text-muted mt-1">URL: <span className="font-mono">/tin-tuc/{v.slug || '...'}</span></div>
              {errors.slug && <div className="text-xs text-danger mt-1">{errors.slug}</div>}
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Mô tả ngắn <span className="text-danger">*</span></div>
              <textarea
                value={v.excerpt}
                onChange={(e) => upd('excerpt', e.target.value)}
                rows={3}
                className="w-full rounded-md border border-border bg-surface px-3.5 py-2.5 text-sm outline-none focus:border-brand-500 resize-none"
                placeholder="Mô tả xuất hiện ở card danh sách và meta description..."
              />
              {errors.excerpt && <div className="text-xs text-danger mt-1">{errors.excerpt}</div>}
            </label>
          </section>

          <section className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
            <div className="flex items-center border-b border-border bg-slate-50">
              <button
                type="button"
                onClick={() => setTab('write')}
                className={[
                  'h-11 px-5 text-sm font-medium border-b-2 transition inline-flex items-center gap-2',
                  tab === 'write' ? 'border-brand-600 text-brand-600 bg-surface' : 'border-transparent text-text hover:text-brand-600',
                ].join(' ')}
              >
                <FileText className="w-4 h-4" /> Nội dung
              </button>
              <button
                type="button"
                onClick={() => setTab('preview')}
                className={[
                  'h-11 px-5 text-sm font-medium border-b-2 transition inline-flex items-center gap-2',
                  tab === 'preview' ? 'border-brand-600 text-brand-600 bg-surface' : 'border-transparent text-text hover:text-brand-600',
                ].join(' ')}
              >
                <Eye className="w-4 h-4" /> Xem trước
              </button>
              <div className="ml-auto px-5 text-xs text-muted">
                {(v.content ?? '').trim().split(/\s+/).filter(Boolean).length} từ · ~{v.readTime} phút đọc
              </div>
            </div>

            {tab === 'write' ? (
              <textarea
                value={v.content}
                onChange={(e) => upd('content', e.target.value)}
                rows={18}
                className="w-full px-5 py-4 text-sm outline-none resize-y font-mono"
                placeholder={`# Mở đầu\n\nViết nội dung bài viết ở đây. Hỗ trợ Markdown cơ bản:\n\n## Tiêu đề phụ\n\n- Gạch đầu dòng 1\n- Gạch đầu dòng 2\n\n**In đậm**, *nghiêng*.\n\n> Trích dẫn...`}
              />
            ) : (
              <div className="p-6 prose prose-sm max-w-none min-h-[400px]">
                {v.content ? (
                  <div className="whitespace-pre-wrap text-sm leading-relaxed text-text">{v.content}</div>
                ) : (
                  <div className="text-muted italic text-center py-16">Chưa có nội dung — gõ vào tab "Nội dung" để xem preview</div>
                )}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-5">
          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Trạng thái & Phân loại</h3>

            <div className="rounded-md bg-slate-50 border border-border px-3 py-2.5 flex items-center justify-between">
              <span className="text-sm text-muted">Trạng thái hiện tại</span>
              {v.status === 'published'
                ? <span className="bg-green-50 text-green-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Đã xuất bản</span>
                : <span className="bg-amber-50 text-amber-700 text-xs font-semibold px-2.5 py-0.5 rounded-full">Nháp</span>}
            </div>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Chuyên mục</div>
              <select value={v.category} onChange={(e) => upd('category', e.target.value)} className={inputCls()}>
                {NEWS_CATEGORIES.filter((c) => c !== 'Tất cả').map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </label>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">Tác giả</div>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                <input value={v.author} onChange={(e) => upd('author', e.target.value)} className={inputCls() + ' !pl-10'} />
              </div>
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Ngày đăng</div>
                <div className="relative">
                  <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                  <input value={v.date} onChange={(e) => upd('date', e.target.value)} className={inputCls() + ' !pl-10'} placeholder="04/11/2026" />
                </div>
              </label>
              <label className="block">
                <div className="text-sm font-medium text-text mb-1.5">Phút đọc</div>
                <div className="relative">
                  <Clock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                  <input type="number" value={v.readTime} onChange={(e) => upd('readTime', Number(e.target.value))} className={inputCls() + ' !pl-10'} />
                </div>
              </label>
            </div>
          </section>

          <section className="bg-surface border border-border rounded-xl shadow-card p-5 space-y-4">
            <h3 className="font-semibold text-text">Ảnh đại diện</h3>

            <label className="block">
              <div className="text-sm font-medium text-text mb-1.5">URL ảnh <span className="text-danger">*</span></div>
              <input value={v.image} onChange={(e) => upd('image', e.target.value)} className={inputCls(errors.image)} placeholder="https://..." />
              {errors.image && <div className="text-xs text-danger mt-1">{errors.image}</div>}
            </label>

            {v.image && (
              <div className="aspect-[16/9] rounded-lg bg-slate-100 border border-border overflow-hidden">
                <img
                  src={v.image}
                  alt="preview"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src =
                      'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%23e2e8f0"/></svg>'
                  }}
                />
              </div>
            )}
          </section>

          {/* Mini preview card */}
          <section className="bg-surface border border-border rounded-xl shadow-card p-5">
            <h3 className="font-semibold text-text mb-3">Xem trước card</h3>
            <article className="bg-slate-50 border border-border rounded-xl overflow-hidden">
              <div className="aspect-[16/9] bg-slate-100 overflow-hidden">
                {v.image && <img src={v.image} alt="" className="w-full h-full object-cover" onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 9"><rect width="16" height="9" fill="%23e2e8f0"/></svg>'
                }} />}
              </div>
              <div className="p-4">
                <span className="inline-block bg-brand-50 text-brand-700 text-[10px] font-semibold px-2 py-0.5 rounded-full uppercase">{v.category}</span>
                <div className="text-sm font-semibold text-text mt-2 line-clamp-2">{v.title || 'Tiêu đề bài viết...'}</div>
                <div className="text-xs text-muted mt-1 line-clamp-2">{v.excerpt || 'Mô tả ngắn...'}</div>
                <div className="flex items-center gap-2 text-[11px] text-muted mt-2">
                  <Calendar className="w-3 h-3" /> {v.date} · {v.author}
                </div>
              </div>
            </article>
          </section>
        </div>
      </div>
    </form>
  )
}
